import { createSelector } from "reselect";

import products from "./products";
import resourceTypes from "./resource-types";
import roles from "./roles";

const permissions = {};

/**
 * Returns all permissions
 * @param {Object} state
 * @returns {Object[]} permissions
 */
permissions.get = state => state.permissions.items;

/**
 * Returns permissions filtered by current Role.
 * @param {Object} state
 * @returns {Object[]} permissions
 */
permissions.getByCurrentRole = createSelector(
  [permissions.get, roles.getCurrent],
  (permissions, currentRole) => {
    if (currentRole && currentRole.permissions) {
      return currentRole.permissions.map(perm =>
        permissions.find(p => p.$uri === perm.$ref)
      );
    }
    return [];
  }
);

/**
 * Return permissions for current role, grouped by product.
 * @param {Object} state
 * @returns {Object[]} permissions
 */
permissions.getByProduct = createSelector(
  [permissions.getByCurrentRole, products.getFiltered, resourceTypes.get],
  (permissions, products, resourceTypes) =>
    products.map(product => {
      const productResourceTypes = resourceTypes.filter(
        res => res.product.$ref === product.$uri
      );
      const permissionsByProduct = [];
      productResourceTypes.forEach(res => {
        permissions.forEach(perm => {
          if (perm && perm.resource_type.$ref === res.$uri) {
            permissionsByProduct.push(perm);
          }
        });
      });
      return { ...product, permissions: permissionsByProduct };
    })
);

/**
 * Return permissions for RBAC product.
 * @param {Object} state
 * @returns {Object[]} permissions
 */
permissions.getForRbac = createSelector(
  [permissions.getByCurrentRole, products.getRbac, resourceTypes.get],
  (permissions, rbacProduct, resourceTypes) => {
    const rbacResourceTypes = resourceTypes.filter(
      res => res.product.$ref === rbacProduct.$uri
    );
    const rbacPermissions = [];
    rbacResourceTypes.forEach(res => {
      permissions.forEach(perm => {
        if (perm && perm.resource_type.$ref === res.$uri) {
          rbacPermissions.push(perm);
        }
      });
    });
    return [{ ...rbacProduct, permissions: rbacPermissions }];
  }
);

/**
 * Returns all permissions for a hydrated policy with the policy's resourceType
 * @param {Object} state
 * @param {Object} props
 * @returns {Object[]} permissions
 */
permissions.getForPolicy = (state, props) => {
  if (props.policy && props.policy.role && props.policy.role.permissions) {
    const perms = props.policy.role.permissions.map(perm =>
      state.permissions.items.find(p => p.$uri === perm.$ref)
    );
    return perms.filter(
      perm => perm.resource_type.$ref === props.policy.resourceType.$uri
    );
  }
  return [];
};

export default permissions;
